import { useState, useEffect, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { loadCities, quickCities, cityOption, fold } from './util/cityData';

// ONE select for "where": the city, with its province written next to it. There is deliberately no second
// province field - picking "Isfahan - Isfahan" fills both, and a country's whole list (its cities, each with
// the province it belongs to) is what you search. The person can still type a city that is not in the list;
// that is saved as typed, without a province, and the field says so.
//
// It edits two stored values the owner keeps as `city` and `province` (the customer's address[] entry):
//   <CityProvinceSelect countryCode={country?.code} city={city} province={province}
//                       onChange={({ city, province }) => ...} />
// A value that came from an older record - a typed city, a province nobody can pick any more - is shown as it
// was stored and kept until the person chooses something else.

const LIMIT = 100;   // India alone has ~3,800 cities: render a hundred, search for the rest

const labelOf = (option) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option.province ? `${option.city} — ${option.province}` : option.city;
};

// Cities that START with what was typed first, then those that merely contain it (or the province does).
// The list arrives most-populous-first, so each group keeps that order. "Isfahan - Isf" (the label of the
// current value, being edited) still finds Isfahan: the dash and commas are just spaces to the search.
function filterCities(options, { inputValue }) {
  const q = fold(inputValue).replace(/\s*[—,]\s*/g, ' ').replace(/\s+/g, ' ');
  if (!q) return options.slice(0, LIMIT);
  const starts = [];
  const contains = [];
  for (const option of options) {
    if (option.fc.startsWith(q)) starts.push(option);
    else if (option.fb.includes(q)) contains.push(option);
    if (starts.length >= LIMIT) break;
  }
  return starts.concat(contains).slice(0, LIMIT);
}

export default function CityProvinceSelect({ countryCode, city, province, onChange, label, sx }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);

  // The short built-in list at once, the country's full list as soon as it has loaded.
  useEffect(() => {
    let alive = true;
    setOptions(countryCode ? quickCities(countryCode) : []);
    if (countryCode) loadCities(countryCode).then((list) => { if (alive) setOptions(list); });
    return () => { alive = false; };
  }, [countryCode]);

  const value = useMemo(() => (city ? cityOption(city, province || '') : null), [city, province]);

  const handleChange = (_, picked) => {
    if (!picked) { onChange({ city: '', province: '' }); return; }
    if (typeof picked === 'string') {
      const typed = picked.trim();
      // Focusing the field and leaving it again hands back the text of the current value: not a new city.
      if (!typed || typed === labelOf(value)) return;
      // A typed name that IS in the list (one place only) picks that place, so "dubai" brings its province too.
      const matches = options.filter((o) => o.fc === fold(typed));
      onChange(matches.length === 1
        ? { city: matches[0].city, province: matches[0].province }
        : { city: typed, province: '' });
      return;
    }
    onChange({ city: picked.city, province: picked.province });
  };

  let hint = '';
  if (!countryCode) hint = t('crm.cityPickCountryFirst');
  else if (city && !province && options.length && !options.some((o) => o.fc === fold(city))) hint = t('crm.cityNotInList');

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      options={options}
      freeSolo
      forcePopupIcon
      autoSelect
      // freeSolo turns this off, and then a value cleared from OUTSIDE (the country changed, the form was reset)
      // leaves the old text sitting in the box while the form holds nothing
      clearOnBlur
      filterOptions={filterCities}
      getOptionLabel={labelOf}
      isOptionEqualToValue={(option, selected) => option.fc === selected.fc && option.fp === selected.fp}
      sx={sx}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: '4px !important' }}>
          <Typography sx={{ fontSize: '0.85rem' }}>{option.city}</Typography>
          {option.province && (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{option.province}</Typography>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} size="small" label={label || t('crm.fieldCity')} sx={sx}
          helperText={hint || undefined}
          FormHelperTextProps={{ sx: { fontSize: '0.68rem', mx: 0.5 } }}
          inputProps={{ ...params.inputProps, style: { fontSize: '0.85rem' } }} />
      )}
    />
  );
}
